// src/components/UpdateFormModal.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaRocket, FaWrench, FaBug,
    FaTimes, FaBold, FaItalic, FaListUl,
    FaListOl, FaQuoteRight, FaCloudUploadAlt,
    FaCheck, FaSpinner
} from 'react-icons/fa';
import './UpdateFormModal.css';

// ─── Categorias ───────────────────────────────────────────────────────────────
const CATEGORIES = [
    {
        id: 'NOVO',
        label: 'Novidade',
        sub: 'Novo recurso ou funcionalidade',
        icon: <FaRocket />,
        cls: 'cat-novo',
    },
    {
        id: 'MELHORIA',
        label: 'Melhoria',
        sub: 'Aprimoramento do existente',
        icon: <FaWrench />,
        cls: 'cat-melhoria',
    },
    {
        id: 'CORRECAO',
        label: 'Correção',
        sub: 'Bug corrigido ou ajuste',
        icon: <FaBug />,
        cls: 'cat-correcao',
    },
];

// ─── Barra de ferramentas do editor ──────────────────────────────────────────
const EditorToolbar = ({ editor }) => {
    if (!editor) return null;

    const tools = [
        { label: 'Negrito',       icon: <FaBold />,       action: () => editor.chain().focus().toggleBold().run(),       active: editor.isActive('bold')        },
        { label: 'Itálico',       icon: <FaItalic />,     action: () => editor.chain().focus().toggleItalic().run(),     active: editor.isActive('italic')      },
        { label: 'Lista',         icon: <FaListUl />,     action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList')  },
        { label: 'Lista Numerada',icon: <FaListOl />,     action: () => editor.chain().focus().toggleOrderedList().run(),active: editor.isActive('orderedList') },
        { label: 'Citação',       icon: <FaQuoteRight />, action: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive('blockquote')  },
    ];

    return (
        <div className="ufm-toolbar">
            {tools.map((t) => (
                <button
                    key={t.label}
                    type="button"
                    title={t.label}
                    className={`ufm-toolbar__btn${t.active ? ' is-active' : ''}`}
                    onClick={t.action}
                >
                    {t.icon}
                </button>
            ))}
        </div>
    );
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function UpdateFormModal({ isOpen, onClose, onSave, updateData }) {
    const [form, setForm]               = useState({ categoria: 'MELHORIA', titulo: '', versao: '' });
    const [descricao, setDescricao]     = useState('');
    const [imageFile, setImageFile]     = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSaving, setIsSaving]       = useState(false);
    const [isDragging, setIsDragging]   = useState(false);
    const fileInputRef                  = useRef(null);

    const editor = useEditor({
        extensions: [StarterKit],
        content: '',
        onUpdate: ({ editor }) => setDescricao(editor.getHTML()),
    });

    // Preenche o form ao abrir
    useEffect(() => {
        setImageFile(null);

        if (updateData) {
            setForm({
                categoria: updateData.categoria || 'MELHORIA',
                titulo:    updateData.titulo    || '',
                versao:    updateData.versao    || '',
            });
            const desc = updateData.descricao || '';
            setDescricao(desc);
            editor?.commands.setContent(desc);
            setImagePreview(updateData.imagem_url || null);
        } else {
            setForm({ categoria: 'MELHORIA', titulo: '', versao: '' });
            setDescricao('');
            editor?.commands.setContent('');
            setImagePreview(null);
        }
    }, [updateData, isOpen, editor]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // Upload via input ou drag-and-drop
    const applyFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleFileChange  = (e)  => applyFile(e.target.files[0]);
    const handleDrop        = (e)  => { e.preventDefault(); setIsDragging(false); applyFile(e.dataTransfer.files[0]); };
    const handleDragOver    = (e)  => { e.preventDefault(); setIsDragging(true);  };
    const handleDragLeave   = ()   => setIsDragging(false);

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append('descricao', descricao);

        if (imageFile) {
            fd.append('imagem', imageFile);
        } else if (updateData && imagePreview) {
            fd.append('imagem_url', imagePreview);
        } else {
            fd.append('imagem_url', '');
        }

        try {
            // Não passe Content-Type manualmente com FormData.
            // Axios detecta o FormData e adiciona o boundary correto:
            // Content-Type: multipart/form-data; boundary=------XXX
            // Sem o boundary o multer não parseia e a rota retorna 404.
            if (updateData) {
                await api.put(`/api/system-hub/updates/${updateData.id}`, fd);
                toast.success('Publicação atualizada!');
            } else {
                await api.post('/api/system-hub/updates', fd);
                toast.success('Publicação criada com sucesso!');
            }
            onSave();
        } catch (err) {
            console.error('[UpdateFormModal] Erro ao salvar:', err?.response?.data || err);
            toast.error(err?.response?.data?.error || 'Erro ao salvar a publicação.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const selectedCat = CATEGORIES.find(c => c.id === form.categoria);

    return (
        <div className="ufm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="ufm-box">
                <form onSubmit={handleSubmit} className="ufm-form">

                    {/* ── Cabeçalho ── */}
                    <div className={`ufm-header ufm-header--${selectedCat?.cls}`}>
                        <div className="ufm-header__left">
                            <div className="ufm-header__icon">
                                {selectedCat?.icon}
                            </div>
                            <div>
                                <p className="ufm-header__eyebrow">
                                    {updateData ? 'Editando publicação' : 'Nova publicação'}
                                </p>
                                <h2 className="ufm-header__title">
                                    {form.titulo || 'Sem título'}
                                </h2>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="ufm-close"
                            onClick={onClose}
                            title="Fechar"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    {/* ── Corpo ── */}
                    <div className="ufm-body">

                        {/* Categoria */}
                        <div className="ufm-field">
                            <label className="ufm-label">Tipo de publicação</label>
                            <div className="ufm-cat-grid">
                                {CATEGORIES.map(cat => (
                                    <label
                                        key={cat.id}
                                        className={`ufm-cat-card ufm-cat-card--${cat.cls}${form.categoria === cat.id ? ' selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="categoria"
                                            value={cat.id}
                                            checked={form.categoria === cat.id}
                                            onChange={handleChange}
                                        />
                                        <span className="ufm-cat-card__check">
                                            <FaCheck />
                                        </span>
                                        <span className="ufm-cat-card__icon">{cat.icon}</span>
                                        <span className="ufm-cat-card__label">{cat.label}</span>
                                        <span className="ufm-cat-card__sub">{cat.sub}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Título + Versão */}
                        <div className="ufm-row">
                            <div className="ufm-field ufm-field--grow">
                                <label className="ufm-label" htmlFor="titulo">
                                    Título <span className="ufm-required">*</span>
                                </label>
                                <input
                                    id="titulo"
                                    type="text"
                                    name="titulo"
                                    className="ufm-input"
                                    value={form.titulo}
                                    onChange={handleChange}
                                    placeholder="Descreva o que mudou…"
                                    required
                                    autoComplete="off"
                                />
                            </div>
                            <div className="ufm-field ufm-field--version">
                                <label className="ufm-label" htmlFor="versao">
                                    Versão <span className="ufm-optional">opcional</span>
                                </label>
                                <input
                                    id="versao"
                                    type="text"
                                    name="versao"
                                    className="ufm-input"
                                    value={form.versao}
                                    onChange={handleChange}
                                    placeholder="v1.5.0"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        {/* Editor de texto */}
                        <div className="ufm-field">
                            <label className="ufm-label">
                                Descrição detalhada <span className="ufm-optional">opcional</span>
                            </label>
                            <div className="ufm-editor-wrap">
                                <EditorToolbar editor={editor} />
                                <div className="ufm-editor-area">
                                    <EditorContent editor={editor} />
                                </div>
                            </div>
                        </div>

                        {/* Upload de imagem */}
                        <div className="ufm-field">
                            <label className="ufm-label">
                                Imagem ilustrativa <span className="ufm-optional">opcional</span>
                            </label>

                            {imagePreview ? (
                                <div className="ufm-img-preview">
                                    <img src={imagePreview} alt="Preview" />
                                    <div className="ufm-img-preview__overlay">
                                        <button
                                            type="button"
                                            className="ufm-img-remove"
                                            onClick={removeImage}
                                        >
                                            <FaTimes /> Remover imagem
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`ufm-dropzone${isDragging ? ' ufm-dropzone--active' : ''}`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="ufm-dropzone__icon">
                                        <FaCloudUploadAlt />
                                    </div>
                                    <p className="ufm-dropzone__title">
                                        {isDragging
                                            ? 'Solte a imagem aqui'
                                            : <><strong>Clique para fazer upload</strong> ou arraste aqui</>}
                                    </p>
                                    <p className="ufm-dropzone__hint">PNG, JPG, GIF — máx. 5MB</p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            )}
                        </div>

                    </div>{/* /ufm-body */}

                    {/* ── Rodapé ── */}
                    <div className="ufm-footer">
                        <button
                            type="button"
                            className="ufm-btn ufm-btn--ghost"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="ufm-btn ufm-btn--primary"
                            disabled={isSaving}
                        >
                            {isSaving
                                ? <><FaSpinner className="ufm-spin" /> Salvando…</>
                                : <><FaCheck /> {updateData ? 'Salvar alterações' : 'Publicar'}</>}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}